/**
 * Schema.org mapping utilities for structured data
 *
 * Maps internal equipment names to Schema.org LocationFeatureSpecification values
 * Used for generating JSON-LD structured data on establishment pages
 */

import { getTourismLabelConfig } from '~/types/tourism-labels'
import { getGiteTourismLabelConfig } from '~/types/gite-tourism-labels'
import { markdownToPlainText } from '~~/shared/markdown-plain-text'

/**
 * Mapping of internal equipment names to Schema.org friendly names
 * Keys are the equipment values stored in database
 * Values are human-readable names for Schema.org amenityFeature
 */
export const EQUIPMENT_TO_SCHEMA_ORG: Record<string, string> = {
  // WiFi
  'wifi': 'WiFi gratuit',
  'Wifi': 'WiFi gratuit',
  'WiFi': 'WiFi gratuit',
  'Wifi gratuit': 'WiFi gratuit',

  // Climatisation
  'climatisation': 'Climatisation',
  'Climatisation': 'Climatisation',

  // Piscine
  'piscine': 'Piscine',
  'Piscine': 'Piscine',

  // Jardin
  'jardin': 'Jardin',
  'Jardin': 'Jardin',

  // Parking
  'parking': 'Parking gratuit',
  'Parking': 'Parking gratuit',
  'parking_ferme': 'Parking fermé',
  'Parking fermé': 'Parking fermé',
  'parking_ouvert': 'Parking',
  'Parking ouvert': 'Parking',
  'garage_velo_moto': 'Garage vélo/moto',
  'Garage vélo/moto': 'Garage vélo/moto',

  // Petit-déjeuner
  'petit_dejeuner_inclus': 'Petit-déjeuner inclus',
  'petit_dejeuner_supplement': 'Petit-déjeuner (supplément)',
  'Petit-déjeuner inclus': 'Petit-déjeuner inclus',
  'Petit-déjeuner supplément': 'Petit-déjeuner (supplément)',

  // Accessibilité
  'PMR': 'Accessible PMR',
  'pmr': 'Accessible PMR',
  'plain_pied': 'Plain-pied',
  'Plain-pied': 'Plain-pied',

  // Animaux
  'animaux_acceptes': 'Animaux acceptés',
  'Animaux acceptés': 'Animaux acceptés',

  // Cuisine
  'Cuisine': 'Cuisine équipée',
  'cuisine': 'Cuisine équipée',
  'Kitchenette': 'Kitchenette',

  // Extérieur
  'Terrasse': 'Terrasse',
  'terrasse': 'Terrasse',
  'Barbecue': 'Barbecue',
  'barbecue': 'Barbecue',

  // Détente
  'Spa': 'Spa',
  'spa': 'Spa',
  'Sauna': 'Sauna',
  'sauna': 'Sauna',
  'Jacuzzi': 'Jacuzzi',
  'jacuzzi': 'Jacuzzi',

  // Équipements chambre
  'TV': 'Télévision',
  'tv': 'Télévision',
  'Télévision': 'Télévision',

  // Catalogue enrichi (4.114.0)
  // Bien-être
  'bienetre_spa_jacuzzi': 'Spa / jacuzzi',
  'bienetre_sauna': 'Sauna',
  'bienetre_hammam': 'Hammam',
  'bienetre_bain_nordique': 'Bain nordique',
  // Extérieur
  'brasero': 'Brasero',
  'salon_jardin': 'Salon de jardin',
  'transats': 'Transats',
  'terrasse_privee': 'Terrasse privée',
  // Mobilité
  'borne_recharge_ve': 'Borne de recharge pour véhicule électrique',
  'velos_disposition': 'Vélos à disposition',
  // Cuisine & intérieur
  'lave_vaisselle': 'Lave-vaisselle',
  'lave_linge': 'Lave-linge',
  'four': 'Four',
  'micro_ondes': 'Micro-ondes',
  'cheminee_poele': 'Cheminée / poêle',
  // Bébé
  'lit_bebe': 'Lit bébé',
  'chaise_haute': 'Chaise haute',

  // Table d'hôtes (utilisé comme constante pour cohérence)
  'table_dhotes': 'Table d\'hôtes',
}

/**
 * Constant for table d'hôtes amenity name
 * Used to maintain consistency between slug.vue and mapping utilities
 */
export const TABLE_DHOTES_AMENITY_NAME = 'Table d\'hôtes'

/**
 * Schema.org ImageObject interface
 */
export interface SchemaOrgImageObject {
  '@type': 'ImageObject'
  'url': string
  'contentUrl': string
  'name': string
  'description'?: string
}

/**
 * Photo input for ImageObject generation
 */
interface PhotoForImageObject {
  url: string
  alt?: string | null
}

/**
 * Builds structured Schema.org ImageObject array from photo data
 *
 * @param photos - Array of photos with url and optional alt text
 * @param baseUrl - Site base URL for resolving relative paths
 * @param establishmentName - Establishment name used as fallback for image name
 * @param photoAltFn - Function to generate photo alt text with index (e.g. i18n)
 * @returns Array of Schema.org ImageObject
 */
export function buildImageObjects(
  photos: PhotoForImageObject[] | undefined | null,
  baseUrl: string,
  establishmentName: string,
  photoAltFn?: (index: number) => string,
): SchemaOrgImageObject[] {
  if (!photos || !Array.isArray(photos)) return []

  return photos.map((p, index) => {
    const absoluteUrl = p.url.startsWith('http') ? p.url : `${baseUrl}${p.url}`
    const fallbackName = photoAltFn
      ? `${establishmentName} — ${photoAltFn(index + 1)}`
      : `${establishmentName} — Photo ${index + 1}`

    return {
      '@type': 'ImageObject' as const,
      'url': absoluteUrl,
      'contentUrl': absoluteUrl,
      'name': p.alt || fallbackName,
      ...(p.alt ? { description: p.alt } : {}),
    }
  })
}

/**
 * LocationFeatureSpecification interface for Schema.org
 */
export interface LocationFeatureSpecification {
  '@type': 'LocationFeatureSpecification'
  name: string
  value: boolean
}

/**
 * Offer interface for Schema.org (room offers)
 */
export interface SchemaOrgOffer {
  '@type': 'Offer'
  name: string
  description?: string
  priceSpecification: {
    '@type': 'UnitPriceSpecification'
    price: number
    priceCurrency: string
    unitText: string
  }
}

/**
 * Room interface for offer generation
 */
interface RoomForOffer {
  name: string
  description?: string | null
  maxAdults: number
  basePricing: { pricePerNight: number } | null
}

/**
 * Generates Schema.org Offer objects from room data.
 * 1 Offer par chambre = prix de base (`room.basePricing.pricePerNight`).
 * Les chambres sans `basePricing` sont skip (pas de prix → pas d'Offer).
 *
 * @example
 * ```ts
 * const offers = generateRoomOffers([
 *   { name: 'Chambre Lavande', maxAdults: 2, basePricing: { pricePerNight: 85 } }
 * ])
 * ```
 */
export function generateRoomOffers(rooms: RoomForOffer[]): SchemaOrgOffer[] {
  if (!rooms || !Array.isArray(rooms)) {
    return []
  }

  const offers: SchemaOrgOffer[] = []
  for (const room of rooms) {
    const pricing = room.basePricing
    if (!pricing || pricing.pricePerNight <= 0) continue
    // Texte brut AVANT la troncature (4.130.8) : la description de chambre est
    // saisie en Markdown, et le JSON-LD n'en rend pas la syntaxe.
    const roomDescription = markdownToPlainText(room.description)
    offers.push({
      '@type': 'Offer',
      name: room.name,
      description: roomDescription
        ? `${roomDescription.slice(0, 100)}${roomDescription.length > 100 ? '...' : ''}`
        : `Chambre pour ${room.maxAdults} personne${room.maxAdults > 1 ? 's' : ''}`,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: pricing.pricePerNight,
        priceCurrency: 'EUR',
        unitText: 'nuit',
      },
    })
  }
  return offers
}

/**
 * Maps an array of equipment names to Schema.org LocationFeatureSpecification format
 *
 * @param equipments - Array of equipment names from the database
 * @param labelOf - Résolveur de libellé localisé (4.144.1), prioritaire s'il
 *   résout la clé (retour non vide et différent de la clé) ; sinon on retombe
 *   sur `EQUIPMENT_TO_SCHEMA_ORG` (FR), puis sur la valeur brute.
 * @returns Array of Schema.org LocationFeatureSpecification objects
 *
 * @example
 * ```ts
 * const amenities = mapEquipmentsToSchemaOrg(['wifi', 'piscine'])
 * // Returns:
 * // [
 * //   { '@type': 'LocationFeatureSpecification', name: 'WiFi gratuit', value: true },
 * //   { '@type': 'LocationFeatureSpecification', name: 'Piscine', value: true }
 * // ]
 * ```
 */
export function mapEquipmentsToSchemaOrg(
  equipments: string[],
  labelOf?: (key: string) => string,
): LocationFeatureSpecification[] {
  if (!equipments || !Array.isArray(equipments)) {
    return []
  }

  // Use a Set to avoid duplicates (e.g., 'wifi' and 'Wifi' both map to 'WiFi gratuit')
  const seen = new Set<string>()

  return equipments
    .map((eq) => {
      const resolved = labelOf?.(eq)
      const mappedName = (resolved && resolved.trim() && resolved !== eq)
        ? resolved
        : (EQUIPMENT_TO_SCHEMA_ORG[eq] || eq)
      if (seen.has(mappedName)) {
        return null
      }
      seen.add(mappedName)
      return {
        '@type': 'LocationFeatureSpecification' as const,
        name: mappedName,
        value: true,
      }
    })
    .filter((item): item is LocationFeatureSpecification => item !== null)
}

/**
 * Map des bedType DB → Schema.org bed labels
 * Permet à Google de comprendre la composition exacte des chambres
 */
export const BED_TYPE_SCHEMA: Record<string, string> = {
  SINGLE: 'Single',
  DOUBLE: 'Double',
  QUEEN: 'Queen',
  KING: 'King',
  BUNK_2: 'Bunk',
  SOFA_BED: 'Sofa bed',
  FUTON_BED: 'Futon',
  TRUNDLE: 'Trundle',
  BABY_CRIB: 'Baby crib',
  CHILD_BED: 'Child bed',
  EXTRA_BED: 'Extra bed',
}

/**
 * Schema.org BedDetails interface
 */
export interface SchemaOrgBedDetails {
  '@type': 'BedDetails'
  'numberOfBeds': number
  'typeOfBed': string
}

/**
 * Schema.org Accommodation interface
 */
export interface SchemaOrgAccommodation {
  '@type': 'Accommodation'
  'name': string
  'description'?: string
  'numberOfRooms': number
  'numberOfBedrooms': number
  'numberOfBathroomsTotal'?: number
  'occupancy'?: {
    '@type': 'QuantitativeValue'
    'value': number
    'unitText': string
  }
  'bed'?: SchemaOrgBedDetails[]
  'image'?: string[]
  'amenityFeature'?: LocationFeatureSpecification[]
  'floorSize'?: {
    '@type': 'QuantitativeValue'
    'value': number
    'unitCode': 'MTK'
  }
}

/**
 * Input for building a Schema.org Accommodation.
 * Primitives only — no Prisma entity, so it stays testable and reusable
 * for any lodging unit (chambre today, gîte later).
 */
export interface AccommodationInput {
  name: string
  description?: string | null
  maxAdults: number
  maxChildren: number
  beds?: { bedType: string, quantity?: number | null }[] | null
  photos?: { url: string | null }[] | null
  equipments?: unknown
  /** Défaut 1 (une chambre est une pièce) */
  numberOfRooms?: number
  /** Défaut 1 */
  numberOfBedrooms?: number
  /** `undefined` → clé omise (chambre sans salle de bain privative) */
  numberOfBathroomsTotal?: number
  /** Surface en m². Omise si absente. */
  floorSize?: number
}

/**
 * Builds a Schema.org Accommodation from a lodging unit.
 *
 * Pattern officiel pour décrire les chambres au sein d'un BedAndBreakfast.
 * Éligible aux rich results "Hôtels" Google et compréhension structurelle par les LLM.
 *
 * L'ordre d'insertion des clés est significatif : il détermine l'ordre de
 * sérialisation du JSON-LD. Ne pas réordonner.
 *
 * @example
 * ```ts
 * buildAccommodation({
 *   name: 'Chambre Lavande',
 *   maxAdults: 2,
 *   maxChildren: 1,
 *   beds: [{ bedType: 'DOUBLE', quantity: 1 }],
 *   numberOfBathroomsTotal: 1,
 * })
 * ```
 */
export function buildAccommodation(input: AccommodationInput): SchemaOrgAccommodation {
  const occupancyValue = input.maxAdults + input.maxChildren

  // `Number.isFinite` plutôt qu'un test `!== undefined` : un null ou un NaN
  // sérialiserait `"key": null` et invaliderait le nœud Accommodation.
  // Le typage explicite est nécessaire — Number.isFinite ne narrow pas.
  const bathrooms: number | undefined = Number.isFinite(input.numberOfBathroomsTotal)
    ? input.numberOfBathroomsTotal
    : undefined
  const floorSize: number | undefined = Number.isFinite(input.floorSize)
    ? input.floorSize
    : undefined

  const beds = (input.beds ?? []).map(b => ({
    '@type': 'BedDetails' as const,
    'numberOfBeds': b.quantity ?? 1,
    'typeOfBed': BED_TYPE_SCHEMA[b.bedType] ?? b.bedType,
  }))

  const images = (input.photos ?? [])
    .map(p => p.url)
    .filter((url): url is string => Boolean(url))

  const equipments = Array.isArray(input.equipments) ? input.equipments as string[] : []
  const amenities = mapEquipmentsToSchemaOrg(equipments)

  return {
    '@type': 'Accommodation' as const,
    'name': input.name,
    ...(input.description ? { description: input.description } : {}),
    'numberOfRooms': input.numberOfRooms ?? 1,
    'numberOfBedrooms': input.numberOfBedrooms ?? 1,
    ...(bathrooms !== undefined ? { numberOfBathroomsTotal: bathrooms } : {}),
    ...(occupancyValue > 0
      ? {
          occupancy: {
            '@type': 'QuantitativeValue' as const,
            'value': occupancyValue,
            'unitText': 'persons',
          },
        }
      : {}),
    ...(beds.length > 0 ? { bed: beds } : {}),
    ...(images.length > 0 ? { image: images } : {}),
    ...(amenities.length > 0 ? { amenityFeature: amenities } : {}),
    // floorSize est ajouté EN DERNIER, jamais intercalé : la séquence de clés
    // historique reste intacte quel que soit l'appelant.
    ...(floorSize !== undefined
      ? {
          floorSize: {
            '@type': 'QuantitativeValue' as const,
            'value': floorSize,
            'unitCode': 'MTK' as const,
          },
        }
      : {}),
  }
}

/**
 * Schema.org CreativeWork interface for awards and credentials
 */
export interface SchemaOrgCredential {
  '@type': 'CreativeWork'
  name: string
  description?: string
  url?: string
  sourceOrganization?: {
    '@type': 'Organization'
    name: string
    url?: string
  }
}

/**
 * Maps tourism labels to Schema.org award and hasCredential structured data
 *
 * Labels with schemaOrgType = 'award' → mapped to award[] field
 * Labels with schemaOrgType = 'hasCredential' → mapped to hasCredential[] field
 *
 * @param tourismLabels - Array of tourism label keys from the database
 * @returns Object with award and hasCredential arrays for Schema.org JSON-LD
 */
export function mapTourismLabelsToSchemaOrg(tourismLabels: string[]): {
  award: SchemaOrgCredential[]
  hasCredential: SchemaOrgCredential[]
} {
  const result: { award: SchemaOrgCredential[], hasCredential: SchemaOrgCredential[] } = {
    award: [],
    hasCredential: [],
  }

  if (!tourismLabels || !Array.isArray(tourismLabels)) {
    return result
  }

  for (const key of tourismLabels) {
    const config = getTourismLabelConfig(key)
    if (!config) continue

    const schemaItem: SchemaOrgCredential = {
      '@type': 'CreativeWork',
      name: config.name,
      description: config.description,
      ...(config.certifierUrl ? { url: config.certifierUrl } : {}),
      sourceOrganization: {
        '@type': 'Organization',
        name: config.certifier,
        ...(config.certifierUrl ? { url: config.certifierUrl } : {}),
      },
    }

    if (config.schemaOrgType === 'award') {
      result.award.push(schemaItem)
    }
    else {
      result.hasCredential.push(schemaItem)
    }
  }

  return result
}

/**
 * Maps GÎTE tourism labels to Schema.org award / hasCredential structured data.
 *
 * Volontairement distincte de `mapTourismLabelsToSchemaOrg` : le référentiel
 * gîte est disjoint (`gite-tourism-labels.ts`) et le classement étoiles Atout
 * France meublé n'a pas d'équivalent côté chambres. Réutiliser le mapping
 * chambres émettrait des credentials incorrects (plan §344). Branchée au Lot 10.
 *
 * @param tourismLabels - Clés de labels gîte stockées en base (`Gite.tourismLabels`)
 */
export function mapGiteTourismLabelsToSchemaOrg(tourismLabels: string[]): {
  award: SchemaOrgCredential[]
  hasCredential: SchemaOrgCredential[]
} {
  const result: { award: SchemaOrgCredential[], hasCredential: SchemaOrgCredential[] } = {
    award: [],
    hasCredential: [],
  }

  if (!tourismLabels || !Array.isArray(tourismLabels)) {
    return result
  }

  for (const key of tourismLabels) {
    const config = getGiteTourismLabelConfig(key)
    if (!config) continue

    const schemaItem: SchemaOrgCredential = {
      '@type': 'CreativeWork',
      name: config.name,
      description: config.description,
      ...(config.certifierUrl ? { url: config.certifierUrl } : {}),
      sourceOrganization: {
        '@type': 'Organization',
        name: config.certifier,
        ...(config.certifierUrl ? { url: config.certifierUrl } : {}),
      },
    }

    if (config.schemaOrgType === 'award') {
      result.award.push(schemaItem)
    }
    else {
      result.hasCredential.push(schemaItem)
    }
  }

  return result
}

export interface SchemaOrgListingPostalAddress {
  '@type': 'PostalAddress'
  'addressLocality'?: string
  'postalCode'?: string
  'addressRegion'?: string
  'addressCountry': 'FR'
}

/**
 * `address` d'un hébergement dans un `ItemList` de listing (accueil, régions,
 * départements, filtres, POI). Google exige `address` sur tout `LocalBusiness`
 * (Semrush : 350 erreurs « address required », audit 2026-10-14).
 *
 * Volontairement SANS `streetAddress` : la rue reste sur la fiche, la liste
 * n'expose que la localisation (décision 4.113.1). Ni ville ni code postal →
 * `undefined`, le nœud est émis sans `address` plutôt qu'avec un pays seul.
 * `region` est du texte libre saisi par le propriétaire : « France » n'est pas
 * une région et n'est pas publié.
 */
export function buildListingPostalAddress(input: {
  city?: string | null
  postalCode?: string | null
  region?: string | null
}): SchemaOrgListingPostalAddress | undefined {
  if (!input.city && !input.postalCode) return undefined
  return {
    '@type': 'PostalAddress',
    ...(input.city ? { addressLocality: input.city } : {}),
    ...(input.postalCode ? { postalCode: input.postalCode } : {}),
    ...(input.region && input.region.trim().toLowerCase() !== 'france' ? { addressRegion: input.region } : {}),
    'addressCountry': 'FR',
  }
}

/**
 * Schema.org LodgingBusiness pour un gîte (fiche voyageur, Lot 10).
 *
 * 🔴 Contraintes SEO non négociables (plan §6, mémoire reference_seo_constraints) :
 * - `@type: 'LodgingBusiness'` — **JAMAIS `VacationRental`** (programme partenaire
 *   Google gated → rejet silencieux + décroissance GSC).
 * - `containsPlace` à **UN SEUL** `Accommodation` : le gîte EST le logement.
 *   Garder le wrapper `LodgingBusiness` porte `aggregateRating`/`address`/`priceRange`
 *   (les rich results).
 * - **AUCUN `makesOffer`/`Offer`/`Product`** : interdit sur le gîte. On ne reproduit
 *   PAS `generateRoomOffers` (qui émet des `Offer`).
 * - `priceRange` normalisé NUIT, format strict `€${prix}` : un `€680` (semaine) face
 *   à un `€85` (chambre) casserait la comparaison inter-types.
 * - Labels via `mapGiteTourismLabelsToSchemaOrg` (référentiel gîte disjoint) — le
 *   mapping chambres émettrait des credentials incorrects = risque de rejet.
 */
export interface GiteLodgingInput {
  name: string
  description?: string | null
  pageUrl: string
  /** ImageObjects déjà construits via `buildImageObjects` */
  images: SchemaOrgImageObject[]
  address?: string | null
  city?: string | null
  region?: string | null
  postalCode?: string | null
  latitude?: number | null
  longitude?: number | null
  /** Prix/nuit normalisé (`priceFrom`). ≤ 0 ou null → `priceRange` omis. */
  priceFrom?: number | null
  nbChambres: number
  nbSallesDeBain: number
  capaciteMax: number
  surface?: number | null
  couchages?: { bedType: string, quantity?: number | null }[] | null
  equipments?: unknown
  /**
   * Résolveur de libellé d'équipement dans la langue de la page (4.144.1).
   * Absent : libellés FR legacy de `EQUIPMENT_TO_SCHEMA_ORG`, puis clé brute.
   */
  equipmentLabel?: (key: string) => string
  tourismLabels?: string[] | null
  googleRating?: number | null
  googleReviewCount?: number | null
}

export interface SchemaOrgLodgingBusiness {
  '@type': 'LodgingBusiness'
  '@id': string
  'url': string
  'name': string
  'description'?: string
  'image'?: SchemaOrgImageObject[]
  'address'?: {
    '@type': 'PostalAddress'
    'streetAddress'?: string
    'addressLocality'?: string
    'addressRegion'?: string
    'postalCode'?: string
    'addressCountry': 'FR'
  }
  'geo'?: { '@type': 'GeoCoordinates', 'latitude': number, 'longitude': number }
  'priceRange'?: string
  'numberOfRooms'?: number
  'amenityFeature'?: LocationFeatureSpecification[]
  'containsPlace'?: SchemaOrgAccommodation[]
  'aggregateRating'?: {
    '@type': 'AggregateRating'
    'ratingValue': number
    'reviewCount'?: number
    'bestRating': number
  }
  'award'?: SchemaOrgCredential[]
  'hasCredential'?: SchemaOrgCredential[]
}

export function buildGiteLodgingBusiness(input: GiteLodgingInput): SchemaOrgLodgingBusiness {
  const equipments = Array.isArray(input.equipments) ? input.equipments as string[] : []
  const amenities = mapEquipmentsToSchemaOrg(equipments, input.equipmentLabel)

  // Le gîte EST le logement → un seul Accommodation. `numberOfBedrooms` = nbChambres,
  // `numberOfBathroomsTotal` = nbSallesDeBain, occupancy = capacité globale (maxChildren=0
  // car le tarif gîte ne distingue pas adulte/enfant — capacité fixe).
  const accommodation = buildAccommodation({
    name: input.name,
    description: input.description,
    maxAdults: input.capaciteMax,
    maxChildren: 0,
    beds: input.couchages,
    numberOfRooms: input.nbChambres,
    numberOfBedrooms: input.nbChambres,
    ...(input.nbSallesDeBain > 0 ? { numberOfBathroomsTotal: input.nbSallesDeBain } : {}),
    ...(input.surface != null ? { floorSize: input.surface } : {}),
  })

  const postalAddress = (input.address || input.city)
    ? {
        '@type': 'PostalAddress' as const,
        ...(input.address ? { streetAddress: input.address } : {}),
        ...(input.city ? { addressLocality: input.city } : {}),
        ...(input.region ? { addressRegion: input.region } : {}),
        ...(input.postalCode ? { postalCode: input.postalCode } : {}),
        'addressCountry': 'FR' as const,
      }
    : undefined

  const aggregateRating = input.googleRating
    ? {
        '@type': 'AggregateRating' as const,
        'ratingValue': input.googleRating,
        ...(input.googleReviewCount != null ? { reviewCount: input.googleReviewCount } : {}),
        'bestRating': 5,
      }
    : undefined

  const { award, hasCredential } = mapGiteTourismLabelsToSchemaOrg(input.tourismLabels ?? [])

  return {
    '@type': 'LodgingBusiness' as const,
    '@id': `${input.pageUrl}#gite`,
    'url': input.pageUrl,
    'name': input.name,
    ...(input.description ? { description: input.description } : {}),
    ...(input.images.length > 0 ? { image: input.images } : {}),
    ...(postalAddress ? { address: postalAddress } : {}),
    ...(input.latitude != null && input.longitude != null
      ? { geo: { '@type': 'GeoCoordinates' as const, 'latitude': input.latitude, 'longitude': input.longitude } }
      : {}),
    ...(input.priceFrom != null && input.priceFrom > 0 ? { priceRange: `€${input.priceFrom}` } : {}),
    ...(input.nbChambres > 0 ? { numberOfRooms: input.nbChambres } : {}),
    ...(amenities.length > 0 ? { amenityFeature: amenities } : {}),
    'containsPlace': [accommodation],
    ...(aggregateRating ? { aggregateRating } : {}),
    ...(award.length > 0 ? { award } : {}),
    ...(hasCredential.length > 0 ? { hasCredential } : {}),
  }
}
